import React, { useCallback, useContext, useMemo, useState } from 'react'
import copy from 'copy-to-clipboard'
import { useHistory, useRouteMatch, useParams } from 'react-router'
import qs from 'qs'

import {
  Dropdown,
  useDropdownState,
  ModalContext,
  Flex,
} from '@sketch/components'
import { isSecondaryClick } from '@sketch/utils'
import { useToast } from '@sketch/toasts'
import {
  QueryParams,
  versionedRoutes,
  routes,
  LaunchSketchAlert,
  isFrameGroupRoute,
  isPageSectionRoute,
  isPreviewFrameCanvasRoute,
} from '@sketch/modules-common'
import { IS_EMBEDDED } from '@sketch/constants'
import { useVersioning } from '../../../../versioning'
import { PartialShareModalCreate } from '../../../modals/PartialShareModal'
import { StyledCanvasContextMenu, AppIcon } from './CanvasContextMenu.styles'
import { ContextMenuMetaData, Coordinates } from './useCanvasContextMenu'
import { INSPECTOR_SEGMENT } from '../../../constants'
import {
  useAnnotationOverlayContext,
  useCreateDraftAnnotation,
} from '../../../../annotations/hooks'

import {
  useShareSidebarTab,
  getActivePanels,
} from '../../../components/ShareSidebarTabContext'
import { usePreview } from '../../../views/PreviewView/PreviewContext'

import { ReactComponent as LinkIcon } from '@sketch/icons/markdown-link-share-17'
import { ReactComponent as FrameIcon } from '@sketch/icons/frame-17'
import { ReactComponent as AddCommentIcon } from '@sketch/icons/comment-chat-bubble-plus-17'
import { ReactComponent as InspectIcon } from '@sketch/icons/code-block-17'
import { ReactComponent as PenIcon } from '@sketch/icons/pen-nib-17'
import { ReactComponent as PreviewIcon } from '@sketch/icons/document-magnifying-glass-17'

type CanvasContextMenuProps = {
  permanentPageId: string
  contextMenuPos: Coordinates | null
  contextMenuMetaData: ContextMenuMetaData | null
  closeTooltip: () => void
}

/**
 * Menu displayed when right clicking on the canvas.
 */
export function CanvasContextMenu({
  contextMenuPos,
  contextMenuMetaData,
  closeTooltip,
  permanentPageId,
}: CanvasContextMenuProps) {
  const { versionShortId, getPathname, share } = useVersioning()
  const previewContext = usePreview()
  const { showToast } = useToast()
  const { showModal } = useContext(ModalContext)
  const { path } = useRouteMatch()
  const history = useHistory()
  const params = useParams<{ frameUUID?: string; sectionUUID?: string }>()

  const userCanInspect = previewContext
    ? previewContext?.preview.inspectEnabled
    : share.userCanInspect

  const activePanels = useMemo(
    () =>
      getActivePanels({
        path,
        canInspect: userCanInspect,
      }),
    [path, userCanInspect]
  )

  const [segments] = useState(activePanels)
  const { toggleSegment } = useShareSidebarTab(segments)

  const annotationsContext = useAnnotationOverlayContext()
  const handleDraftAnnotationEvent = useCreateDraftAnnotation(
    share?.identifier || ''
  )

  const dropdown = useDropdownState({
    onToggle: closeTooltip,
  })

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (isSecondaryClick(event)) {
        return
      }
      closeTooltip()
    },
    [closeTooltip]
  )

  if (contextMenuPos === null || !contextMenuMetaData) {
    return null
  }

  function handleInpsectCanvas() {
    toggleSegment('Inspect')
    closeTooltip()
  }

  function handleCopyLinkToCoordinates() {
    if (!share) {
      return null
    }

    // We want to always use a versioned route (rather than implicitly linking
    // to the latest version) so that URLs are not affected when a new version
    // is created.
    let currentPathname
    if (isFrameGroupRoute(path)) {
      currentPathname = versionedRoutes.FRAME.VERSION.create({
        frameUUID: params.frameUUID ?? '',
        shareID: share.identifier,
        versionShortId,
      })
    } else if (isPageSectionRoute(path)) {
      currentPathname = versionedRoutes.PAGE_SECTION.VERSION.create({
        pageUUID: permanentPageId,
        sectionUUID: params.sectionUUID ?? '',
        shareID: share.identifier,
        versionShortId,
      })
    } else if (permanentPageId) {
      // Only include a pageUUID if the user has navigated away from the first page
      currentPathname = versionedRoutes.SHARE_PAGE_CANVAS_VIEW.VERSION.create({
        pageUUID: permanentPageId,
        shareID: share.identifier,
        versionShortId,
      })
    } else {
      currentPathname = versionedRoutes.SHARE_VIEW.VERSION.create({
        shareID: share.identifier,
        versionShortId,
      })
    }

    const queryParams: QueryParams<'SHARE_PAGE_CANVAS_VIEW'> = {
      posX: contextMenuMetaData!.canvasPanPosition.posX.toString(),
      posY: contextMenuMetaData!.canvasPanPosition.posY.toString(),
      zoom: contextMenuMetaData!.zoom.toString(),
    }

    const link = `${window.location.origin}${currentPathname}?${qs.stringify(
      queryParams
    )}`
    copy(link)
    showToast('Link copied')
    closeTooltip()
  }

  function handleCopyLinkToPreview() {
    if (!previewContext) {
      return
    }

    let currentPathname
    if (isPreviewFrameCanvasRoute(path)) {
      currentPathname = routes.PREVIEW_FRAME_VIEW.create({
        previewID: previewContext.preview.identifier,
        pageUUID: permanentPageId,
        frameUUID: params.frameUUID ?? '',
      })
    } else {
      currentPathname = routes.PREVIEW_PAGE_VIEW.create({
        previewID: previewContext.preview.identifier,
        pageUUID: permanentPageId,
      })
    }

    const link = `${window.location.origin}${currentPathname}`
    copy(link)
    showToast('Link copied')
    closeTooltip()
  }

  function handleCopyLinkToFrame() {
    if (!contextMenuMetaData?.artboardUUID || (!share && !previewContext)) {
      return
    }

    if (previewContext) {
      const currentPathname = getPathname({
        routeKey: 'PREVIEW_FRAME_VIEW',
        routeParams: {
          previewID: previewContext.preview.identifier,
          pageUUID: permanentPageId,
          frameUUID: contextMenuMetaData.artboardUUID,
        },
      })

      const link = `${window.location.origin}${currentPathname}`
      copy(link)
    } else {
      const currentPathname = getPathname({
        routeKey: 'FRAME',
        routeParams: {
          shareID: share.identifier,
          frameUUID: contextMenuMetaData.artboardUUID,
        },
      })

      const link = `${window.location.origin}${currentPathname}`
      copy(link)
    }

    showToast('Link copied')
    closeTooltip()
  }

  function handleEdit() {
    if (!share) {
      return
    }

    closeTooltip()
    showModal(LaunchSketchAlert, {
      shareId: share.identifier,
      pageUUID: permanentPageId,
      action: 'openInSketch',
    })
  }

  function handleInspectFrame() {
    if (!contextMenuMetaData?.artboardUUID || (!share && !previewContext)) {
      return
    }

    if (previewContext) {
      const currentPathname = getPathname({
        routeKey: 'PREVIEW_FRAME_VIEW',
        routeParams: {
          previewID: previewContext.preview.identifier,
          pageUUID: permanentPageId,
          frameUUID: contextMenuMetaData.artboardUUID,
        },
      })

      closeTooltip()
      history.push(`${currentPathname}#${INSPECTOR_SEGMENT}`)
    } else {
      const currentPathname = getPathname({
        routeKey: 'FRAME',
        routeParams: {
          shareID: share.identifier,
          frameUUID: contextMenuMetaData.artboardUUID,
        },
      })

      closeTooltip()
      history.push(`${currentPathname}#${INSPECTOR_SEGMENT}`)
    }
  }

  const handleAddAnnotation = () => {
    if (handleDraftAnnotationEvent) {
      handleDraftAnnotationEvent(
        {
          clientX: contextMenuPos.posX,
          clientY: contextMenuPos.posY,
        },
        true
      )
    }
    closeTooltip()
  }

  const hasFrameUUID = contextMenuMetaData.artboardUUID !== null

  const dropdownTitle = hasFrameUUID ? 'Frame' : 'Canvas'
  const isFrameCanvas =
    isFrameGroupRoute(path) || isPreviewFrameCanvasRoute(path)
  const showViewFrame =
    !isFrameCanvas && userCanInspect && hasFrameUUID && !IS_EMBEDDED
  const showInspect = userCanInspect && !IS_EMBEDDED
  const showCreatePreview =
    !isFrameCanvas && share && share.userCanUpdateSettings

  if (previewContext) {
    return (
      <StyledCanvasContextMenu
        {...dropdown}
        placement="bottom-start"
        spacing="0px"
        style={{
          left: contextMenuPos.posX,
          top: contextMenuPos.posY,
        }}
        visible
        onClickOutside={handleClickOutside}
      >
        {contextMenuMetaData.artboardUUID !== null ? (
          <>
            <Dropdown.Header>Frame</Dropdown.Header>
            <Dropdown.Item icon={<LinkIcon />} onClick={handleCopyLinkToFrame}>
              Copy Link
            </Dropdown.Item>
          </>
        ) : (
          <>
            <Dropdown.Header>Preview</Dropdown.Header>
            <Dropdown.Item
              icon={<LinkIcon />}
              onClick={handleCopyLinkToPreview}
            >
              Copy Link
            </Dropdown.Item>
          </>
        )}
        {showViewFrame && (
          <Dropdown.Item icon={<FrameIcon />} onClick={handleInspectFrame}>
            View Frame
          </Dropdown.Item>
        )}

        {(showInspect || previewContext.preview.commentsEnabled) && (
          <Dropdown.Divider />
        )}

        {showInspect && (
          <Dropdown.Item icon={<InspectIcon />} onClick={handleInpsectCanvas}>
            Inspect
          </Dropdown.Item>
        )}

        {previewContext.preview.commentsEnabled &&
          annotationsContext?.isViewingLatestVersion &&
          (!isFrameCanvas || hasFrameUUID) && (
            <Dropdown.Item
              icon={<AddCommentIcon />}
              onClick={handleAddAnnotation}
            >
              Add Comment…
            </Dropdown.Item>
          )}
      </StyledCanvasContextMenu>
    )
  }

  return (
    <StyledCanvasContextMenu
      {...dropdown}
      placement="bottom-start"
      spacing="0px"
      style={{
        left: contextMenuPos.posX,
        top: contextMenuPos.posY,
      }}
      visible={true}
      onClickOutside={handleClickOutside}
    >
      <Dropdown.Header>{dropdownTitle}</Dropdown.Header>

      {contextMenuMetaData.artboardUUID !== null && (
        <Dropdown.Item icon={<LinkIcon />} onClick={handleCopyLinkToFrame}>
          Copy Link
        </Dropdown.Item>
      )}

      {share ? (
        <Dropdown.Item
          icon={<LinkIcon />}
          onClick={handleCopyLinkToCoordinates}
        >
          Copy Link from Position
        </Dropdown.Item>
      ) : null}

      {showViewFrame && (
        <Dropdown.Item icon={<FrameIcon />} onClick={handleInspectFrame}>
          View Frame
        </Dropdown.Item>
      )}

      {share && (share.commentsEnabled || share.userCanEdit) && (
        <Dropdown.Divider />
      )}

      {share &&
        share.commentsEnabled &&
        annotationsContext?.isViewingLatestVersion &&
        (!isFrameCanvas || hasFrameUUID) && (
          <Dropdown.Item
            icon={<AddCommentIcon />}
            onClick={handleAddAnnotation}
          >
            Add Comment…
          </Dropdown.Item>
        )}

      {showInspect && (
        <Dropdown.Item icon={<InspectIcon />} onClick={handleInpsectCanvas}>
          Inspect
        </Dropdown.Item>
      )}

      {share && share.userCanEdit && (
        <Dropdown.Item icon={<PenIcon />} onClick={handleEdit}>
          <Flex alignItems="center">
            Edit in <AppIcon alt="Sketch" />
          </Flex>
        </Dropdown.Item>
      )}

      {share && showCreatePreview && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item
            icon={<PreviewIcon />}
            onClick={() => {
              showModal(PartialShareModalCreate, {
                shareName: share.name,
                shareIdentifier: share.identifier,
                versionShortId,
              })
              closeTooltip()
            }}
          >
            Share a Preview of this Page…
          </Dropdown.Item>
        </>
      )}
    </StyledCanvasContextMenu>
  )
}

export default CanvasContextMenu
